import { Show, SimpleForm } from 'react-admin';
import { Box, Typography, Paper, Chip, Alert, Grid, Card, CardContent, Button, Avatar, IconButton, CircularProgress } from '@mui/material';
import { Link } from 'react-router-dom';
import { School, CheckCircle, Cancel, Warning, Close, HourglassEmpty, TrendingUp, ArrowForward, Add, Category, Business } from '@mui/icons-material';
import { PageDescription } from '@/components/help';
import { useNavigate, useUpdate, useNotify, useRefresh } from 'react-admin';
import type { CourseRecord } from '@/types/entities';

export const CourseShow = () => {
  const navigate = useNavigate();
  const [update, { isLoading }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();

  return (
    <Show render={({ record }) => {
      if (!record) return null;
      const r = record as CourseRecord;

      const stages = ['Active', 'Archived'];

      const handleArchive = () => {
        update(
          'courses',
          { id: r.id || r.id, data: { status: 'Archived' }, previousData: r },
          {
            onSuccess: () => {
              notify('Course archived successfully', { type: 'success' });
              refresh();
            },
            onError: (error: Error) => {
              notify(`Error: ${error.message}`, { type: 'error' });
            },
          }
        );
      };

      const handleReactivate = () => {
        update(
          'courses',
          { id: r.id || r.id, data: { status: 'Active' }, previousData: r },
          {
            onSuccess: () => {
              notify('Course reactivated successfully', { type: 'success' });
              refresh();
            },
            onError: (error: Error) => {
              notify(`Error: ${error.message}`, { type: 'error' });
            },
          }
        );
      };

      return (
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <IconButton onClick={() => navigate('/courses')} size="small" sx={{ color: 'text.secondary' }}>
              <Close />
            </IconButton>
          </Box>
          <PageDescription entity="Course" description="A training course offered by a provider. Defines content, duration, and accreditation." icon={<School />} />
          {r.status === 'Archived' && (
            <Alert severity="warning" sx={{ mb: 2 }} icon={<Cancel />}>
              This course is archived and not available for new training interventions
            </Alert>
          )}
          {r.status === 'PendingAccreditation' && (
            <Alert severity="info" sx={{ mb: 2 }} icon={<Warning />}>
              Course is pending accreditation verification
            </Alert>
          )}

          <Paper elevation={0} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #2e7d32, #388e3c)', color: 'primary.contrastText', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: 24 }}>
                <School sx={{ fontSize: 32 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" fontWeight={700}>{r.courseTitle}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip label={r.status} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                  <Chip label={`NQF Level ${r.nqfLevel}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                  <Chip label={r.deliveryMode} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                </Box>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>Course ID</Typography>
                <Typography variant="h6" fontWeight={700}>{r.id?.split(':')[1]?.substring(0, 8).toUpperCase() || 'N/A'}</Typography>
              </Box>
            </Box>
          </Paper>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            {stages.map(stage => (
              <Card
                key={stage}
                sx={{
                  flex: 1,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  opacity: r.status === stage ? 1 : 0.55,
                  border: r.status === stage ? 2 : 0,
                  borderColor: 'primary.main',
                  '&:hover': { opacity: 1, transform: 'translateY(-2px)' },
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <HourglassEmpty color={r.status === stage ? 'primary' : 'disabled'} sx={{ fontSize: 32, mb: 1 }} />
                  <Typography variant="subtitle2" fontWeight={700}>{stage}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {r.status === stage ? 'Current stage' : 'Click to view'}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            {r.status === 'Active' && (
              <Button
                variant="contained"
                sx={{ bgcolor: 'primary.main' }}
                startIcon={<Cancel />}
                onClick={handleArchive}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={20} /> : 'Archive Course'}
              </Button>
            )}
            {r.status === 'Archived' && (
              <Button
                variant="contained"
                sx={{ bgcolor: 'primary.main' }}
                startIcon={<CheckCircle />}
                onClick={handleReactivate}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={20} /> : 'Reactivate'}
              </Button>
            )}
          </Box>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Paper sx={{ p: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <School color="primary" />
                  <Typography variant="subtitle1" fontWeight={700}>Course Details</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="caption" color="text.secondary">SETA Qualification Code</Typography>
                    <Typography variant="body1" fontWeight={500}>{r.setaQualificationCode || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="caption" color="text.secondary">Unit Standard Code</Typography>
                    <Typography variant="body1">{r.unitStandardCode || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="caption" color="text.secondary">NQF Level</Typography>
                    <Chip label={`Level ${r.nqfLevel}`} size="small" sx={{ mt: 0.5 }} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="caption" color="text.secondary">Credit Value</Typography>
                    <Typography variant="body1">{r.creditValue} credits</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="caption" color="text.secondary">Duration</Typography>
                    <Typography variant="body1">{r.durationHours} hours {r.durationDays ? `(${r.durationDays} days)` : ''}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="caption" color="text.secondary">Delivery Mode</Typography>
                    <Chip label={r.deliveryMode} size="small" sx={{ mt: 0.5 }} />
                  </Grid>
                </Grid>
              </Paper>

              {r.courseDescription && (
                <Paper sx={{ p: 3, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Course Description</Typography>
                  <Typography variant="body2" color="text.secondary">{r.courseDescription}</Typography>
                </Paper>
              )}

              {r.ofocodes && r.ofocodes.length > 0 && (
                <Paper sx={{ p: 3, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Category color="primary" />
                    <Typography variant="subtitle1" fontWeight={700}>Linked OFO Codes</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {r.ofocodes.map((ofo: { type: string; id: string }) => (
                      <Chip
                        key={ofo.id}
                        label={ofo.id}
                        component={Link}
                        to="/skills-development/ofocodes"
                        clickable
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Paper>
              )}
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Business color="primary" />
                  <Typography variant="subtitle1" fontWeight={700}>Training Provider</Typography>
                </Box>
                {r.trainingProvider ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Chip
                      component={Link}
                      to={`/training-providers/${r.trainingProvider.id}`}
                      clickable
                      label={r.trainingProvider.id}
                      icon={<ArrowForward />}
                      sx={{ justifyContent: 'flex-start' }}
                    />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No provider linked</Typography>
                )}
              </Paper>

              <Paper sx={{ p: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TrendingUp color="primary" />
                  <Typography variant="subtitle1" fontWeight={700}>Quick Actions</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Chip
                    component={Link}
                    to="/training"
                    clickable
                    label="View Training Records"
                    icon={<School />}
                    sx={{ justifyContent: 'flex-start' }}
                  />
                  <Chip
                    component={Link}
                    to={`/training/create?courseId=${encodeURIComponent(r.id)}&courseName=${encodeURIComponent(r.courseTitle)}`}
                    clickable
                    label="Schedule Training"
                    icon={<Add />}
                    sx={{ justifyContent: 'flex-start' }}
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      );
    }} />
  );
};
